import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { translateUi } from './i18n/index';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import {
  LayoutDashboard,
  KeyRound,
  TrainFront,
  Award,
  MapIcon,
  History,
  Calendar,
  GraduationCap,
  Stethoscope,
  HeartPulse,
  Megaphone,
  FolderFile,
  ClipboardList,
  MessageCircle,
  ChevronDown,
  ChevronRight,
  ShieldCheck,
  Briefcase,
  Eye,
  FileText,
} from './icons';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type EmployeeProfileNavTab =
  | 'Main'
  | "Licence"
  | "Cab Passes"
  | "Certificate"
  | "Trains & Routes"
  | "Experience Records"
  | "Cycles"
  | "Training"
  | "Monitoring"
  | "Medical"
  | "Medication Checks"
  | "Safety Briefs"
  | "Driver Reports"
  | "Leave & Absence"
  | "Documents"
  | "Tasks"
  | "Notes";

type MegaCategoryId = 'overview' | 'medical' | 'licensing' | 'operations' | 'development' | 'workspace';

type IconComponent = React.FC<React.SVGProps<SVGSVGElement>>;

type MegaMenuItem = {
  tab: EmployeeProfileNavTab;
  description: string;
  Icon: IconComponent;
  accent: string;
  /** Purchased/activated company add-on — only appears when included in `tabs`. */
  isAddon?: boolean;
};

type MegaMenuCategory = {
  id: MegaCategoryId;
  label: string;
  blurb: string;
  Icon: IconComponent;
  accent: string;
  items: MegaMenuItem[];
  /** Always show the hover panel (even with a single item) so items sit under the category heading. */
  forcePanel?: boolean;
};

const MEGA_MENU_CATEGORIES: MegaMenuCategory[] = [
  {
    id: 'overview',
    label: 'Overview',
    blurb: 'Profile snapshot and core employee status',
    Icon: LayoutDashboard,
    accent: 'sky',
    items: [
      {
        tab: 'Main',
        description: 'Summary, status, and key employee details',
        Icon: LayoutDashboard,
        accent: 'sky',
      },
    ],
  },
  {
    id: 'medical',
    label: 'Medical',
    blurb: 'Medical fitness and medication checks',
    Icon: Stethoscope,
    accent: 'emerald',
    forcePanel: true,
    items: [
      { tab: "Medical", description: 'Medical fitness and health records', Icon: Stethoscope, accent: 'emerald' },
      {
        tab: "Medication Checks",
        description: 'Medication declarations and OH med checks',
        Icon: HeartPulse,
        accent: 'emerald',
        isAddon: true,
      },
    ],
  },
  {
    id: 'licensing',
    label: 'Licensing',
    blurb: 'Licences and certificates',
    Icon: ShieldCheck,
    accent: 'indigo',
    items: [
      { tab: "Licence", description: 'Driver licence details and expiry', Icon: KeyRound, accent: 'indigo' },
      { tab: "Certificate", description: 'Complementary certificate (Part B) details', Icon: Award, accent: 'amber' },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    blurb: 'Cab passes, routes, experience, cycles, safety briefs, and driver reports',
    Icon: Briefcase,
    accent: 'cyan',
    items: [
      { tab: "Cab Passes", description: 'Cab pass authority and validity', Icon: TrainFront, accent: 'sky' },
      { tab: "Trains & Routes", description: 'Traction and route competence', Icon: MapIcon, accent: 'cyan' },
      { tab: "Experience Records", description: 'Driving experience and event counts', Icon: History, accent: 'slate' },
      { tab: "Cycles", description: 'Assessment cycles and progress', Icon: Calendar, accent: 'violet' },
      {
        tab: "Safety Briefs",
        description: 'Safety briefings and acknowledgements',
        Icon: Megaphone,
        accent: 'amber',
        isAddon: true,
      },
      {
        tab: "Driver Reports",
        description: 'Signed operational driver incident reports',
        Icon: ClipboardList,
        accent: 'cyan',
        isAddon: true,
      },
      {
        tab: "Leave & Absence",
        description: 'Annual leave, sickness, and absence audit trail',
        Icon: Calendar,
        accent: 'amber',
        isAddon: true,
      },
    ],
  },
  {
    id: 'development',
    label: 'Development',
    blurb: 'Training and performance monitoring',
    Icon: GraduationCap,
    accent: 'violet',
    items: [
      { tab: "Training", description: 'Training and qualifications', Icon: GraduationCap, accent: 'violet' },
      { tab: "Monitoring", description: 'Incidents and performance monitoring', Icon: Eye, accent: 'rose' },
    ],
  },
  {
    id: 'workspace',
    label: 'Workspace',
    blurb: 'Documents, tasks, and messaging notes',
    Icon: FileText,
    accent: 'slate',
    items: [
      { tab: "Documents", description: 'Files and supporting documentation', Icon: FolderFile, accent: 'sky' },
      {
        tab: "Tasks",
        description: 'Assigned actions and follow-ups',
        Icon: ClipboardList,
        accent: 'violet',
        isAddon: true,
      },
      { tab: "Notes", description: 'Messaging threads and employee notes', Icon: MessageCircle, accent: 'slate' },
    ],
  },
];

function AddonPill({ isDarkMode = false }: { isDarkMode?: boolean }) {
  return (
    <span
      className={cn(
        'employee-mega-addon-pill inline-flex items-center rounded-full px-1.5 py-0.5 text-[0.62rem] font-bold uppercase tracking-wide',
        isDarkMode
          ? 'bg-amber-500/20 text-amber-200 ring-1 ring-amber-400/30'
          : 'bg-amber-50 text-amber-800 ring-1 ring-amber-200/80'
      )}
    >
      {translateUi('Add-on')}
    </span>
  );
}

const ACCENT_STYLES: Record<
  string,
  { iconBg: string; iconText: string; chip: string; bar: string; glow: string }
> = {
  sky: {
    iconBg: 'bg-sky-50',
    iconText: 'text-sky-700',
    chip: 'bg-sky-100 text-sky-800',
    bar: 'bg-sky-500',
    glow: 'shadow-sky-500/20',
  },
  indigo: {
    iconBg: 'bg-indigo-50',
    iconText: 'text-indigo-700',
    chip: 'bg-indigo-100 text-indigo-800',
    bar: 'bg-indigo-500',
    glow: 'shadow-indigo-500/20',
  },
  cyan: {
    iconBg: 'bg-cyan-50',
    iconText: 'text-cyan-700',
    chip: 'bg-cyan-100 text-cyan-800',
    bar: 'bg-cyan-500',
    glow: 'shadow-cyan-500/20',
  },
  violet: {
    iconBg: 'bg-violet-50',
    iconText: 'text-violet-700',
    chip: 'bg-violet-100 text-violet-800',
    bar: 'bg-violet-500',
    glow: 'shadow-violet-500/20',
  },
  amber: {
    iconBg: 'bg-amber-50',
    iconText: 'text-amber-700',
    chip: 'bg-amber-100 text-amber-800',
    bar: 'bg-amber-500',
    glow: 'shadow-amber-500/20',
  },
  emerald: {
    iconBg: 'bg-emerald-50',
    iconText: 'text-emerald-700',
    chip: 'bg-emerald-100 text-emerald-800',
    bar: 'bg-emerald-500',
    glow: 'shadow-emerald-500/20',
  },
  rose: {
    iconBg: 'bg-rose-50',
    iconText: 'text-rose-700',
    chip: 'bg-rose-100 text-rose-800',
    bar: 'bg-rose-500',
    glow: 'shadow-rose-500/20',
  },
  slate: {
    iconBg: 'bg-slate-100',
    iconText: 'text-slate-700',
    chip: 'bg-slate-200 text-slate-800',
    bar: 'bg-slate-500',
    glow: 'shadow-slate-500/20',
  },
};

const DARK_ACCENT_STYLES: Record<
  string,
  { iconBg: string; iconText: string; chip: string; bar: string; glow: string }
> = {
  sky: {
    iconBg: 'bg-sky-500/15',
    iconText: 'text-sky-300',
    chip: 'bg-sky-500/20 text-sky-200',
    bar: 'bg-sky-400',
    glow: 'shadow-sky-400/15',
  },
  indigo: {
    iconBg: 'bg-indigo-500/15',
    iconText: 'text-indigo-300',
    chip: 'bg-indigo-500/20 text-indigo-200',
    bar: 'bg-indigo-400',
    glow: 'shadow-indigo-400/15',
  },
  cyan: {
    iconBg: 'bg-cyan-500/15',
    iconText: 'text-cyan-300',
    chip: 'bg-cyan-500/20 text-cyan-200',
    bar: 'bg-cyan-400',
    glow: 'shadow-cyan-400/15',
  },
  violet: {
    iconBg: 'bg-violet-500/15',
    iconText: 'text-violet-300',
    chip: 'bg-violet-500/20 text-violet-200',
    bar: 'bg-violet-400',
    glow: 'shadow-violet-400/15',
  },
  amber: {
    iconBg: 'bg-amber-500/15',
    iconText: 'text-amber-300',
    chip: 'bg-amber-500/20 text-amber-200',
    bar: 'bg-amber-400',
    glow: 'shadow-amber-400/15',
  },
  emerald: {
    iconBg: 'bg-emerald-500/15',
    iconText: 'text-emerald-300',
    chip: 'bg-emerald-500/20 text-emerald-200',
    bar: 'bg-emerald-400',
    glow: 'shadow-emerald-400/15',
  },
  rose: {
    iconBg: 'bg-rose-500/15',
    iconText: 'text-rose-300',
    chip: 'bg-rose-500/20 text-rose-200',
    bar: 'bg-rose-400',
    glow: 'shadow-rose-400/15',
  },
  slate: {
    iconBg: 'bg-slate-500/20',
    iconText: 'text-slate-300',
    chip: 'bg-slate-500/25 text-slate-200',
    bar: 'bg-slate-400',
    glow: 'shadow-slate-400/15',
  },
};

function getTabLabel(tab: EmployeeProfileNavTab) {
  if (tab === 'Training') return translateUi('Training & Qualifications');
  if (tab === 'Certificate') return translateUi('Complementary Certificate (Part B)');
  if (tab === 'Notes') return translateUi('Employee Messaging & Notes');
  if (tab === 'Monitoring') return translateUi('Monitoring & Incidents');
  return translateUi(tab);
}

function findCategoryForTab(tab: EmployeeProfileNavTab): MegaCategoryId {
  for (const category of MEGA_MENU_CATEGORIES) {
    if (category.items.some((item) => item.tab === tab)) return category.id;
  }
  return 'overview';
}

type EmployeeProfileMegaMenuProps = {
  tabs: EmployeeProfileNavTab[];
  activeTab: EmployeeProfileNavTab;
  onSelectTab: (tab: EmployeeProfileNavTab, closeMobileMenu?: boolean) => void;
  isDarkMode?: boolean;
  isMobileOrTablet?: boolean;
  isTablet?: boolean;
  mobileOpen?: boolean;
  onMobileOpenChange?: (open: boolean) => void;
};

function canHoverFinePointer(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  } catch {
    return true;
  }
}

export function EmployeeProfileMegaMenu({
  tabs,
  activeTab,
  onSelectTab,
  isDarkMode = false,
  isMobileOrTablet = false,
  isTablet = false,
  mobileOpen = false,
  onMobileOpenChange,
}: EmployeeProfileMegaMenuProps) {
  const [openCategoryId, setOpenCategoryId] = useState<MegaCategoryId | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<MegaCategoryId | null>(() => findCategoryForTab(activeTab));
  const [hoverCapable, setHoverCapable] = useState(canHoverFinePointer);
  const rootRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);

  const availableTabs = useMemo(() => new Set(tabs), [tabs]);

  const categories = useMemo(
    () =>
      MEGA_MENU_CATEGORIES.map((category) => ({
        ...category,
        items: category.items.filter((item) => availableTabs.has(item.tab)),
      })).filter((category) => category.items.length > 0),
    [availableTabs]
  );

  const activeCategoryId = findCategoryForTab(activeTab);
  const activeCategory = categories.find((c) => c.id === activeCategoryId) ?? categories[0];
  const panelCategory = categories.find((c) => c.id === openCategoryId) ?? null;

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => setOpenCategoryId(null), 160);
  }, [clearCloseTimer]);

  const revealCategory = useCallback(
    (id: MegaCategoryId) => {
      clearCloseTimer();
      setOpenCategoryId(id);
    },
    [clearCloseTimer]
  );

  useEffect(() => {
    return () => clearCloseTimer();
  }, [clearCloseTimer]);

  useEffect(() => {
    if (!openCategoryId) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenCategoryId(null);
    };
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpenCategoryId(null);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [openCategoryId]);

  useEffect(() => {
    setMobileExpanded(findCategoryForTab(activeTab));
  }, [activeTab]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setHoverCapable(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const accentPack = isDarkMode ? DARK_ACCENT_STYLES : ACCENT_STYLES;

  const handleCategoryTrigger = (category: (typeof categories)[number]) => {
    const isSingle = category.items.length === 1 && !category.forcePanel;
    if (isSingle) {
      onSelectTab(category.items[0].tab);
      setOpenCategoryId(null);
      return;
    }
    setOpenCategoryId((prev) => (prev === category.id ? null : category.id));
  };

  if (isMobileOrTablet) {
    return (
      <div className={cn('employee-mega-menu employee-mega-menu--mobile', isTablet && 'employee-mega-menu--tablet')} ref={rootRef}>
        <button
          type="button"
          className="employee-mega-mobile-trigger"
          onClick={() => onMobileOpenChange?.(!mobileOpen)}
          aria-expanded={mobileOpen}
          aria-controls="employee-profile-mega-mobile"
          aria-label={translateUi("Toggle employee section navigation")}
        >
          <span className="employee-mega-mobile-trigger-main">
            <span className={cn('employee-mega-mobile-icon', accentPack[activeCategory?.accent ?? 'sky'].iconBg, accentPack[activeCategory?.accent ?? 'sky'].iconText)}>
              {activeCategory ? <activeCategory.Icon className="h-4 w-4" /> : <LayoutDashboard className="h-4 w-4" />}
            </span>
            <span className="employee-mega-mobile-copy">
              <span className="employee-mega-mobile-kicker">{activeCategory?.label ?? 'Section'}</span>
              <span className="employee-mega-mobile-title">{getTabLabel(activeTab)}</span>
            </span>
          </span>
          <ChevronDown className={cn('h-4 w-4 shrink-0 transition-transform duration-200', mobileOpen && 'rotate-180')} />
        </button>

        <div
          id="employee-profile-mega-mobile"
          className={cn('employee-mega-mobile-panel', mobileOpen && 'open')}
          role="navigation"
          aria-label={translateUi("Employee details sections")}
        >
          {categories.map((category) => {
            const isExpanded = mobileExpanded === category.id;
            const hasActive = category.items.some((item) => item.tab === activeTab);
            const catAccent = accentPack[category.accent];
            return (
              <div key={category.id} className={cn('employee-mega-mobile-group', hasActive && 'is-active')}>
                <button
                  type="button"
                  className="employee-mega-mobile-group-btn"
                  onClick={() => setMobileExpanded((prev) => (prev === category.id ? null : category.id))}
                  aria-expanded={isExpanded}
                >
                  <span className={cn('employee-mega-item-icon', catAccent.iconBg, catAccent.iconText)}>
                    <category.Icon className="h-4 w-4" />
                  </span>
                  <span className="employee-mega-mobile-group-copy">
                    <span className="employee-mega-mobile-group-label">{translateUi(category.label)}</span>
                    <span className="employee-mega-mobile-group-blurb">{translateUi(category.blurb)}</span>
                  </span>
                  <ChevronDown className={cn('h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200', isExpanded && 'rotate-180')} />
                </button>
                <div className={cn('employee-mega-mobile-items', isExpanded && 'open')}>
                  {category.items.map((item) => {
                    const itemAccent = accentPack[item.accent];
                    const isActive = activeTab === item.tab;
                    return (
                      <button
                        key={item.tab}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        className={cn('employee-mega-link', isActive && 'active')}
                        onClick={() => onSelectTab(item.tab, true)}
                      >
                        <span className={cn('employee-mega-item-icon', itemAccent.iconBg, itemAccent.iconText)}>
                          <item.Icon className="h-4 w-4" />
                        </span>
                        <span className="employee-mega-link-copy">
                          <span className="employee-mega-link-title-row">
                            <span className="employee-mega-link-title">{getTabLabel(item.tab)}</span>
                            {item.isAddon && <AddonPill isDarkMode={isDarkMode} />}
                          </span>
                          <span className="employee-mega-link-desc">{translateUi(item.description)}</span>
                        </span>
                        {isActive ? <span className={cn('employee-mega-active-dot', itemAccent.bar)} /> : <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-40" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div
      className="employee-mega-menu employee-mega-menu--desktop"
      ref={rootRef}
      onMouseLeave={hoverCapable ? scheduleClose : undefined}
      onMouseEnter={hoverCapable ? clearCloseTimer : undefined}
    >
      <div className="employee-mega-bar" role="menubar" aria-label={translateUi("Employee details sections")}>
        {categories.map((category) => {
          const isActiveCategory = category.id === activeCategoryId;
          const isOpen = openCategoryId === category.id;
          const catAccent = accentPack[category.accent];
          const isSingle = category.items.length === 1 && !category.forcePanel;

          return (
            <div
              key={category.id}
              className="employee-mega-trigger-wrap"
              onMouseEnter={() => {
                if (!hoverCapable) return;
                if (!isSingle) revealCategory(category.id);
                else {
                  clearCloseTimer();
                  setOpenCategoryId(null);
                }
              }}
            >
              <button
                type="button"
                role="menuitem"
                aria-haspopup={isSingle ? undefined : 'true'}
                aria-expanded={isSingle ? undefined : isOpen}
                className={cn(
                  'employee-mega-trigger',
                  isActiveCategory && 'active',
                  isOpen && 'open'
                )}
                onClick={() => handleCategoryTrigger(category)}
              >
                <span className={cn('employee-mega-trigger-icon', catAccent.iconBg, catAccent.iconText)}>
                  <category.Icon className="h-3.5 w-3.5" />
                </span>
                <span className="employee-mega-trigger-label">{translateUi(category.label)}</span>
                {!isSingle && (
                  <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 transition-transform duration-200', isOpen && 'rotate-180')} />
                )}
                {isActiveCategory && <span className={cn('employee-mega-trigger-underline', catAccent.bar)} />}
              </button>
            </div>
          );
        })}

        <div className="employee-mega-current">
          <span className="employee-mega-current-kicker">{translateUi("Now viewing")}</span>
          <span className="employee-mega-current-value">{getTabLabel(activeTab)}</span>
        </div>
      </div>

      <div
        className={cn('employee-mega-panel', panelCategory && 'open')}
        role="menu"
        aria-hidden={!panelCategory}
        onMouseEnter={clearCloseTimer}
      >
        {panelCategory && (
          <div className="employee-mega-panel-inner" key={panelCategory.id}>
            <div className="employee-mega-panel-header">
              <div className="employee-mega-panel-header-copy">
                <p className="employee-mega-panel-eyebrow">{translateUi(panelCategory.label)}</p>
                <p className="employee-mega-panel-blurb">{translateUi(panelCategory.blurb)}</p>
              </div>
              <span className={cn('employee-mega-panel-count', accentPack[panelCategory.accent].chip)}>
                {panelCategory.items.length}{' '}
                {panelCategory.items.length === 1 ? translateUi('section') : translateUi('sections')}
              </span>
            </div>

            <div
              className={cn(
                'employee-mega-grid',
                panelCategory.items.length <= 2 && 'cols-2',
                panelCategory.items.length === 3 && 'cols-3',
                panelCategory.items.length >= 4 && 'cols-4'
              )}
            >
              {panelCategory.items.map((item, index) => {
                const itemAccent = accentPack[item.accent];
                const isActive = activeTab === item.tab;
                return (
                  <button
                    key={item.tab}
                    type="button"
                    role="menuitem"
                    className={cn('employee-mega-card', isActive && 'active', itemAccent.glow)}
                    style={{ animationDelay: `${index * 35}ms` }}
                    onClick={() => {
                      onSelectTab(item.tab);
                      setOpenCategoryId(null);
                    }}
                  >
                    <span className={cn('employee-mega-item-icon employee-mega-item-icon--lg', itemAccent.iconBg, itemAccent.iconText)}>
                      <item.Icon className="h-5 w-5" />
                    </span>
                    <span className="employee-mega-card-copy">
                      <span className="employee-mega-card-title-row">
                        <span className="employee-mega-card-title">{getTabLabel(item.tab)}</span>
                        {item.isAddon && <AddonPill isDarkMode={isDarkMode} />}
                      </span>
                      <span className="employee-mega-card-desc">{translateUi(item.description)}</span>
                    </span>
                    <ChevronRight className="employee-mega-card-arrow h-4 w-4" />
                    {isActive && <span className={cn('employee-mega-card-active-bar', itemAccent.bar)} />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
